/**
 @format
 @flow
 */

import React from 'react';
import styled from 'styled-components';
import Headline from '@ziynx/components/headlines';
import { SubHeading } from '@ziynx/components/typography';
import { VideoSquare } from '@ziynx/components/media';
import type { Props as HeadlineType } from '@ziynx/components/headlines/HeadlineDefault';
import Colors from '@ziynx/theme/colors';
import type { Viewports } from '@ziynx/flow-types/globals';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import CardBase from './CardBase';

export type Props = $ReadOnly<{|
     headline?: HeadlineType,
     data: Array<Object>,
     viewport?: Viewports,

     /** Optional override to flex-direction: column */
     flexColumn?: boolean,

     /** To allow rendering of more cards for specific pages */
     unlimitedCards?: boolean,
     /**
      * Prop defining headline location
      * "top" or "bottom"
      */
     headlineLocation?: string,
     /** subheading */
     subheading?: string,
 |}>;

const _findItemMarginRight = (itemQty) => {
  if (itemQty === 3) return '24px';
  if (itemQty === 2) return '40px';
};

const HeadlineWrapper = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 17px;
    gap: 4px;
 `;

const ContentWrapper = styled.div`
     max-width: 688px;
 `;

const VideoGroupItemWrapper = styled.div`
     width: 100%;
     display: flex;
     flex-direction: column;
     justify-content: space-between;
     margin-right: ${({ totalItems, lastChild, isDesktop }) => (lastChild || !isDesktop ? 0 : _findItemMarginRight(totalItems))};
     margin-bottom: ${({ lastChild, isDesktop, flexColumn }) => (lastChild || (isDesktop && !flexColumn) ? 0 : '32px')};
 `;

const StyledHeadline = styled(Headline)`
    min-height: 24px;
    height: auto;
    padding-top: 16px;
    div:first-child {
      display: flex;
      justify-content: center;
    }
    h3:first-child {
      margin-right: 0;
    }
 `;

const StyledCardBase = styled(CardBase)`
     box-sizing: border-box;
     display: flex;
     flex-direction: ${({ isDesktop, flexColumn }) => (isDesktop && !flexColumn ? 'row' : 'column')};
 `;

const CardVideoSquareGroup = (props: ManagedProps<Props>): React$Node => {
  const {
    headline,
    data: rawData,
    viewport,
    flexColumn,
    unlimitedCards,
    headlineLocation,
    subheading,
  } = props;

  const isDesktop = viewport === 'desktopSmall' || viewport === 'desktop';
  let data = rawData || [];
  if (rawData?.length > 3 && !unlimitedCards) {
    data = rawData?.slice(0, 3);
  }

  const renderItemBottomHeadline = (item, index) => {
    const {
      headline: itemHeadline,
      title,
      subtitle,
      video,
      // eslint-disable-next-line camelcase
      thumbnail_optimized,
      thumbnail,
    } = item;

    const { uri, ...restVideoProps } = video || {};

    return (
      <VideoGroupItemWrapper
        key={`Video_${index + 1}`}
        totalItems={data?.length}
        isDesktop={isDesktop}
        flexColumn={flexColumn}
        lastChild={index === data?.length - 1}
      >
        <VideoSquare
          {...restVideoProps}
          videoSource={uri}
          imageSource={thumbnail_optimized?.uri || thumbnail?.uri}
        />
        {!!headline && (
          <StyledHeadline
            {...itemHeadline}
            enableHover
            height="auto"
            title={{
              text: itemHeadline?.title?.text || title,
              color: itemHeadline?.title?.color || Colors.GrayVeryDark,
              onClick: itemHeadline?.title?.onClick,
              href: itemHeadline?.title?.href,
              ...itemHeadline?.title,
            }}
            subtitle={subtitle}
          />
        )}
      </VideoGroupItemWrapper>
    );
  };

  const renderItemTopHeadline = (item, index) => {
    const {
      headline: itemHeadline,
      title,
      subtitle,
      video,
      // eslint-disable-next-line camelcase
      thumbnail_optimized,
      thumbnail,
    } = item;

    const { uri, ...restVideoProps } = video || {};

    return (
      <VideoGroupItemWrapper
        key={`Video_${index + 1}`}
        totalItems={data?.length}
        isDesktop={isDesktop}
        flexColumn={flexColumn}
        lastChild={index === data?.length - 1}
      >
        <HeadlineWrapper>
          <Headline
            {...itemHeadline}
            enableHover
            title={{
              text: itemHeadline?.title?.text || title,
              color: itemHeadline?.title?.color || Colors.GrayVeryDark,
              onClick: itemHeadline?.title?.onClick,
              href: itemHeadline?.title?.href,
              ...itemHeadline?.title,
            }}
            subtitle={subtitle}
          />
          {!!subheading && (<SubHeading>{subheading}</SubHeading>)}
        </HeadlineWrapper>
        <VideoSquare
          {...restVideoProps}
          videoSource={uri}
          imageSource={thumbnail_optimized?.uri || thumbnail?.uri}
        />
      </VideoGroupItemWrapper>
    );
  };

  return (
    <ContentWrapper>
      <HeadlineWrapper>
        <Headline {...headline} />
      </HeadlineWrapper>
      <StyledCardBase
        width="auto"
        flexColumn={flexColumn}
        isDesktop={isDesktop}
      >
        {(!!data?.length && headlineLocation === 'bottom') ? data.map((item, index) => renderItemBottomHeadline(item, index))
          : data.map((item, index) => renderItemTopHeadline(item, index))}
      </StyledCardBase>
    </ContentWrapper>
  );
};

export default CardVideoSquareGroup;
